/**
 * @license
 * Copyright 2026 okayest! studios LLC. All Rights Reserved.
 */

/**
 * @fileoverview Owns row counter state, persisted to localStorage and kept in
 * sync across tabs.
 */

import { RowCounter, RowCounterState, TOTAL_ROWS_LABEL, getCounter, startCount } from './counter-model';
import { newId } from './id';

export type CounterListener = (state: RowCounterState) => void;

export interface CounterFields {
  label: string;
  rows: number;
  parentId: string | null;
  rowsInRepeat: number | null;
  startAtOne: boolean;
}

const storageKey = 'yarnly.rowCounters';
const counterIdLength = 5;

let state: RowCounterState = loadState();
const listeners = new Set<CounterListener>();

window.addEventListener('storage', (event) => {
  if (event.key !== storageKey) return;
  state = loadState();
  notifyListeners();
});

export function getCounterState(): RowCounterState {
  return state;
}

export function addCounterListener(listener: CounterListener): void {
  listeners.add(listener);
}

export function removeCounterListener(listener: CounterListener): void {
  listeners.delete(listener);
}

/** Adjusts a counter's own rows by [delta], never dropping below its start count. */
export function incrementCounter(id: string, delta: number): boolean {
  const counter = getCounter(state, id);
  if (counter === null) return false;
  const rows = counter.rows + delta;
  if (rows < startCount(counter)) return false;
  updateCounter(id, { rows });
  return true;
}

export function resetCounter(id: string): void {
  const counter = getCounter(state, id);
  if (counter === null) return;
  updateCounter(id, { rows: startCount(counter) });
}

export function addCounter(fields: CounterFields): RowCounter {
  const counter: RowCounter = { ...fields, id: newId(counterIdLength), updated: Date.now() };
  setState({ ...state, counters: [...state.counters, counter] });
  return counter;
}

export function updateCounter(id: string, fields: Partial<CounterFields>): void {
  setState({
    ...state,
    lastUsedCounterId: id,
    counters: state.counters.map((c) => (c.id === id ? { ...c, ...fields, updated: Date.now() } : c)),
  });
}

/** Deletes a counter, unlinking any subcounters from it. */
export function deleteCounter(id: string): void {
  setState({
    ...state,
    lastUsedCounterId: state.lastUsedCounterId === id ? null : state.lastUsedCounterId,
    counters: state.counters
      .filter((c) => c.id !== id)
      .map((c) => (c.parentId === id ? { ...c, parentId: null } : c)),
  });
}

function setState(newState: RowCounterState): void {
  state = newState;
  try {
    localStorage.setItem(storageKey, JSON.stringify(state));
  } catch (err) {
    console.error(`Failed to save row counters: ${storageKey}`, err);
  }
  notifyListeners();
}

function notifyListeners(): void {
  for (const listener of listeners) {
    listener(state);
  }
}

function loadState(): RowCounterState {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(storageKey);
  } catch (err) {
    console.error(`Failed to read row counters: ${storageKey}`, err);
  }
  if (raw !== null) {
    try {
      return parseState(JSON.parse(raw));
    } catch (err) {
      console.error(`Failed to parse row counters: ${storageKey}`, err);
    }
  }
  return {
    counters: [
      {
        id: newId(counterIdLength),
        label: TOTAL_ROWS_LABEL,
        rows: 0,
        parentId: null,
        rowsInRepeat: null,
        startAtOne: false,
        updated: Date.now(),
      },
    ],
    lastUsedCounterId: null,
  };
}

function parseState(json: unknown): RowCounterState {
  if (typeof json !== 'object' || json === null) {
    throw new Error('Invalid row counter state');
  }
  const obj = json as Record<string, unknown>;
  const counters = Array.isArray(obj.counters) ? obj.counters.flatMap(parseCounter) : [];
  const ids = new Set(counters.map((c) => c.id));
  // Linked counters only nest one level deep.
  const topLevelIds = new Set(counters.filter((c) => c.parentId === null).map((c) => c.id));
  for (const c of counters) {
    if (c.parentId !== null && !topLevelIds.has(c.parentId)) c.parentId = null;
  }
  const lastUsed = typeof obj.lastUsedCounterId === 'string' ? obj.lastUsedCounterId : null;
  return {
    counters,
    lastUsedCounterId: lastUsed !== null && ids.has(lastUsed) ? lastUsed : null,
  };
}

function parseCounter(json: unknown): RowCounter[] {
  if (typeof json !== 'object' || json === null) return [];
  const c = json as Record<string, unknown>;
  if (typeof c.id !== 'string') return [];
  const startAtOne = c.startAtOne === true;
  const start = startAtOne ? 1 : 0;
  const rowsInRepeat = isInt(c.rowsInRepeat) && c.rowsInRepeat > 0 ? c.rowsInRepeat : null;
  return [
    {
      id: c.id,
      label: typeof c.label === 'string' ? c.label : '',
      rows: isInt(c.rows) && c.rows >= start ? c.rows : start,
      parentId: typeof c.parentId === 'string' && c.parentId !== c.id ? c.parentId : null,
      rowsInRepeat,
      startAtOne,
      updated: isInt(c.updated) ? c.updated : 0,
    },
  ];
}

function isInt(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value);
}
